/**
 * Data transformation utilities for Threats Dashboard
 * Transforms raw API data into the format expected by the DataTable component
 */

// Vision UI Dashboard PRO React components
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import VuiBadge from 'components/VuiBadge';

// React Router
import { Link } from 'react-router-dom';

// React icons
import { IoWarning } from 'react-icons/io5';

// API Services
import { takedownsApi } from 'services/takedownsApi';

/**
 * Extract unique root domains from platform URLs
 * @param {string} platforms - Comma-separated platform URLs
 * @returns {Array} - Array of unique root domains
 */
const extractUniqueDomains = (platforms) => {
  if (!platforms) return [];
  
  const platformUrls = platforms.split(',').map(url => url.trim());
  const rootDomains = platformUrls.map(url => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch (e) {
      // If URL parsing fails, try to extract domain manually
      const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^\/]+)/);
      return match ? match[1] : url;
    }
  });
  
  return [...new Set(rootDomains)];
};

/**
 * Transform raw threats API data into DataTable format
 * @param {Object} apiResponse - The response from the threats API
 * @returns {Promise<Object>} - DataTable compatible format
 */
export const transformThreatsData = async (apiResponse) => {
  const { data, pagination, filters, applied_filters, sorting } = apiResponse;

  // Fetch infringement types for all threats in parallel
  const infringementTypesPromises = data.map(item => 
    takedownsApi.getDistinctInfringementTypes(item.id)
      .catch(err => {
        console.warn(`Failed to fetch infringement types for threat ${item.id}:`, err);
        return ['Unknown'];
      })
  );
  
  const allInfringementTypes = await Promise.all(infringementTypesPromises);
  
  // Transform the data array
  const transformedData = data.map((item, index) => ({
    threatActor: (() => {
      const username = item.username;
      const isAnonymousOrNull = !username || 
        username.toLowerCase() === 'null' || 
        username.toLowerCase() === 'n/a' || 
        username.toLowerCase() === 'anonymous';
      
      if (isAnonymousOrNull) {
        return (
          <VuiTypography variant="button" color="text" fontWeight="regular">
            n/a
          </VuiTypography>
        );
      }
      
      return (
        <Link to={`/dashboards/threats/${item.id || 'unknown'}/details`} style={{ textDecoration: 'none', color:"white"}}>
          <VuiTypography
         
            variant="button"
            color="white"
            fontWeight="medium"
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {username}
          </VuiTypography>

        </Link>
       
      );
    })(),

    infringementType: (() => {
      const types = allInfringementTypes[index] || ['Unknown'];
      const displayText = types.length > 0 ? types.join(', ') : 'Unknown';
      
      return (
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {displayText}
        </VuiTypography>
      );
    })(),

    platformCount: (() => {
      const uniqueDomains = extractUniqueDomains(item.platforms);
      
      return (
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {uniqueDomains.length}
        </VuiTypography>
      );
    })(),

    totalTakedowns: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {item.num_detected || 0}
      </VuiTypography>
    ),

    successRate: (
      <VuiBox display="flex" alignItems="center" gap={1}>
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {item.num_submitted > 0 ? `${Math.round((item.num_removed / item.num_submitted) * 100)}%` : 'N/A'}
        </VuiTypography>
        {item.num_submitted > 0 && (item.num_removed / item.num_submitted) < 0.5 && (
          <IoWarning color="#ff6b6b" size={16} />
        )}
      </VuiBox>
    ),

    lastActivity: (
      <VuiTypography variant="button" color="white" fontWeight="regular">
        {item.num_pending > 0 ? 'Active' : 'Inactive'}
      </VuiTypography>
    ),

    platforms: (() => {
      const uniqueDomains = extractUniqueDomains(item.platforms);
      
      if (uniqueDomains.length === 0) {
        return (
          <VuiTypography variant="button" color="white" fontWeight="regular">
            N/A
          </VuiTypography>
        );
      }
      
      return (
        <VuiBox display="flex" flexDirection="column" gap={0.5}>
          {uniqueDomains.map((domain, index) => (
            <Link to={`/dashboards/platforms/${encodeURIComponent(domain)}/details`} style={{ textDecoration: 'none', color:"white"}}>
              <VuiTypography
              key={index}
              fontWeight="medium"
              variant="button"
              color="white"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {domain}
            </VuiTypography>
            </Link>
            
          ))}
        </VuiBox>
      );
    })(),

    // Store raw data for sorting and filtering
    _rawData: item,
  }));

  return {
    columns: [
      { 
        Header: 'THREAT ACTOR', 
        accessor: 'threatActor', 
        width: '15%',
      },
      { 
        Header: 'INFRINGEMENT TYPE', 
        accessor: 'infringementType', 
        width: '16%',
      },
      { 
        Header: 'TOTAL PLATFORMS', 
        accessor: 'platformCount', 
        width: '16%',
      },
      { 
        Header: '# TAKEDOWNS SUBMITTED', 
        accessor: 'totalTakedowns', 
        width: '16%',
      },
      { 
        Header: 'SUCCESS RATE', 
        accessor: 'successRate', 
        width: '16%',
      },
      { 
        Header: 'ACTIVITY STATUS', 
        accessor: 'lastActivity', 
        width: '16%',
      },
      { 
        Header: 'PLATFORMS', 
        accessor: 'platforms', 
        width: '16%',
      },
    ],
    rows: transformedData,
    pagination: pagination,
    filters: filters,
    applied_filters: applied_filters,
    sorting: sorting,
  };
};

/**
 * Get filter buttons with counts for the threats dashboard
 * @param {Object} counts - Count data from the API
 * @param {Function} onFilterChange - Callback for filter changes
 * @param {string} activeFilter - Currently active filter
 * @returns {Array} - Array of filter button objects
 */
export const getThreatFilterButtons = (counts, onFilterChange, activeFilter) => {
  if (!counts?.breakdown?.by_violation_type) {
    return [
      { key: 'Explicit', label: 'Explicit', filter: { violation_type: 'Explicit' } },
      { key: 'NILV', label: 'NILV', filter: { violation_type: 'NILV' } }
    ];
  }

  return counts.breakdown.by_violation_type.map((item) => ({
    key: item.violation_type,
    label: `${item.violation_type} (${item.count})`,
    filter: { violation_type: item.violation_type },
    count: item.count,
  }));
};

/**
 * Create empty state for threats dashboard
 * @returns {Object} - Empty DataTable format
 */
export const createEmptyThreatsData = () => ({
  columns: [
    { Header: 'THREAT ACTOR', accessor: 'threatActor', width: '15%' },
    { Header: 'INFRINGEMENT TYPE', accessor: 'infringementType', width: '16%' },
    { Header: 'TOTAL PLATFORMS', accessor: 'platformCount', width: '16%' },
    { Header: '# TAKEDOWNS SUBMITTED', accessor: 'totalTakedowns', width: '16%' },
    { Header: 'SUCCESS RATE', accessor: 'successRate', width: '16%' },
    { Header: 'LAST ACTIVITY', accessor: 'lastActivity', width: '16%' },
    { Header: 'PLATFORMS', accessor: 'platforms', width: '16%' },
  ],
  rows: [],
  pagination: {
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
    has_next_page: false,
    has_prev_page: false,
  },
  filters: {
    // No violation_type filter for threats
  },
  applied_filters: {
    search: null,
  },
  sorting: {
    sort_by: 'num_detected',
    sort_order: 'DESC',
  },
});
